import { useState } from "react";
import { Package, SlidersHorizontal, ChevronRight, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { photoThumb, photoThumbBackend } from "@/lib/photoUrl";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const STORE_KEY = "quote_items_mobile_fields";
const DEFAULT_FIELDS = ["size", "price", "qty_per_ctn", "qty40"];

const readFields = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
    return Array.isArray(raw) && raw.length ? raw : DEFAULT_FIELDS;
  } catch {
    return DEFAULT_FIELDS;
  }
};

const fmtCost = (r) => {
  const cur = r.cost_currency === "USD" || (!r.cost_currency && (r.factory_cost_usd || 0) > 0) ? "USD" : "RMB";
  const val = cur === "USD" ? r.factory_cost_usd : r.factory_cost;
  return val ? `${Number(val).toFixed(2)} ${cur}` : "-";
};

/**
 * 2026-06 · 报价单产品明细 —— 手机端只读卡片流（外销/管理员/客户）。
 * 桌面宽表在手机上无法阅读，这里改成大图卡片 + 可自选字段（本机记忆），
 * 点卡片打开全屏明细。编辑仍然留在电脑端（用户要求）。
 */
export default function QuoteItemsMobile({ detail, showCost = true }) {
  const { t } = useLanguage();
  const [fields, setFields] = useState(readFields);
  const [fieldSheet, setFieldSheet] = useState(false);
  const [openItem, setOpenItem] = useState(null);

  const cur = detail.currency === "CAD" ? "C$" : "US$";
  const ROW_FIELDS = [
    ["size", t("quote.size"), (r) => r.size || "-"],
    ["price", `${t("priceList.price")} (${cur})`, (r) => (r.final_price ? Number(r.final_price).toFixed(2) : "-")],
    ["calc_price", `${t("quote.calcPrice")} (${detail.currency})`, (r) => (r.customer_price ? r.customer_price.toFixed(3) : "-")],
    ["qty_per_ctn", t("priceList.qtyPerCtn"), (r) => r.qty_per_ctn || "-"],
    ["qty40", t("quote.qty"), (r) => r.container_qty_40 || "-"],
    ...(showCost ? [["factory_cost", t("priceList.factoryCost"), fmtCost]] : []),
  ];
  const active = ROW_FIELDS.filter(([k]) => fields.includes(k));

  const toggle = (key) => setFields((f) => {
    const next = f.includes(key) ? f.filter((k) => k !== key) : [...f, key];
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
    return next;
  });

  const thumb = (p, size) => {
    const src = p.quote_photo_url || p.composite_photo_url || p.photo_url;
    if (!src) return null;
    return (
      <img
        src={photoThumb(src, size)}
        alt={p.item_number}
        className="w-full h-full object-contain"
        onError={(e) => {
          const i = e.currentTarget;
          if (i.dataset.f !== "1") { i.dataset.f = "1"; i.src = photoThumbBackend(src, size); }
        }}
      />
    );
  };

  return (
    <div className="md:hidden space-y-2" data-testid="quote-items-mobile">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {(detail.items || []).length} {t("nav.products")}
        </span>
        <Button variant="outline" size="sm" className="ml-auto h-9 rounded-md text-xs"
          onClick={() => setFieldSheet(true)} data-testid="quote-mobile-fields-button">
          <SlidersHorizontal size={14} /> {t("quote.m.fields")}
        </Button>
      </div>

      {(detail.items || []).map((p) => (
        <button
          key={p.item_number}
          type="button"
          onClick={() => setOpenItem(p)}
          data-testid={`quote-item-mobile-card-${p.item_number}`}
          className="w-full text-left bg-card border border-border rounded-md p-2.5 flex gap-3 active:bg-secondary/40 transition-colors"
        >
          <div className="w-24 h-24 shrink-0 rounded-md border border-border bg-white flex items-center justify-center overflow-hidden">
            {thumb(p, "md") || <Package size={22} className="text-muted-foreground" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="font-mono text-sm font-bold text-brand truncate">{p.item_number}</span>
              {p.has_order && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-700 font-bold shrink-0">
                  {t("quote.hasOrderBadge")}
                </span>
              )}
              <ChevronRight size={15} className="ml-auto shrink-0 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">{p.description || "-"}</p>
            <div className="mt-1 space-y-0.5">
              {(p.price_rows || []).slice(0, 3).map((r) => (
                <div key={r.id} className="flex flex-wrap gap-x-2 text-[11px]">
                  {active.map(([k, label, get]) => (
                    <span key={k} className="min-w-0">
                      <span className="text-muted-foreground">{k === "size" ? "" : `${label}: `}</span>
                      <span className="font-mono font-semibold">{get(r)}</span>
                    </span>
                  ))}
                </div>
              ))}
              {(p.price_rows || []).length > 3 && (
                <p className="text-[10px] text-brand">+{(p.price_rows || []).length - 3}</p>
              )}
            </div>
          </div>
        </button>
      ))}

      {/* 字段自选 */}
      <Sheet open={fieldSheet} onOpenChange={setFieldSheet}>
        <SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto" data-testid="quote-mobile-fields-sheet">
          <SheetHeader><SheetTitle>{t("quote.m.fields")}</SheetTitle></SheetHeader>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {ROW_FIELDS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => toggle(k)}
                data-testid={`quote-mobile-field-${k}`}
                className={`h-11 px-3 rounded-md border text-xs font-medium flex items-center gap-1.5 transition-colors ${
                  fields.includes(k) ? "border-brand bg-brand/10 text-brand" : "border-border text-muted-foreground"}`}>
                {fields.includes(k) && <Check size={13} />}<span className="truncate">{label}</span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* 产品明细 */}
      <Sheet open={Boolean(openItem)} onOpenChange={(o) => { if (!o) setOpenItem(null); }}>
        <SheetContent side="bottom" className="h-[92vh] overflow-y-auto" data-testid="quote-mobile-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-mono text-base">{openItem?.item_number}</SheetTitle>
          </SheetHeader>
          {openItem && (
            <div className="mt-3 space-y-3">
              <div className="w-full h-56 rounded-md border border-border bg-white flex items-center justify-center overflow-hidden">
                {thumb(openItem, "lg") || <Package size={30} className="text-muted-foreground" />}
              </div>
              {[[t("products.description"), openItem.description],
                [t("products.material"), openItem.material],
                ...(openItem.has_order ? [[t("quote.orderNumber"), openItem.order_number]] : [])]
                .filter(([, v]) => v).map(([label, v]) => (
                <div key={label}>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
                  <p className="text-sm whitespace-pre-wrap">{v}</p>
                </div>
              ))}
              <div className="space-y-2">
                {(openItem.price_rows || []).map((r) => (
                  <div key={r.id} className="rounded-md border border-border p-2.5" data-testid={`quote-mobile-row-${r.id}`}>
                    <p className="font-mono text-sm font-bold">{r.size || "-"}</p>
                    <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                      {ROW_FIELDS.filter(([k]) => k !== "size").map(([k, label, get]) => (
                        <div key={k} className="flex gap-1 min-w-0">
                          <span className="text-muted-foreground shrink-0">{label}:</span>
                          <span className="font-mono font-semibold truncate">{get(r)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground pb-6">{t("quote.m.editOnDesktop")}</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
